/* eslint-disable no-underscore-dangle */
import { setIsStaff } from 'pages/SignIn/redux';
import { useLocalRoomUpdate } from 'pages/app/hooks';
import { useLogs } from 'pages/Logs/hooks';
import {
  BranchFiles,
  getBranchFile,
  selectLogs,
  selectActivityLogsMergedLogs,
  setBranchFiles,
  setIsLoadingLogs,
  setLogs,
  setLogsChecked,
} from 'pages/Logs/redux';
import { rewriteLogsForDepartmentRename } from 'pages/Logs/logUtils';
import { aggregateLogsToMetricLogs } from 'pages/Metrics/aggregateMetricLogs';
import { setMetricLogs } from 'pages/Metrics/redux';
import { useUpdateSettings } from 'pages/Settings/hooks';
import { setCRMSettings, addOrUpdateCustomer, getInfo, Customer } from 'pages/Settings/redux';
import {
  OrganizationData, selectOrganizationData, setOrganizationData, setIsSocketConnected,
  setSocket
} from 'pages/redux';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'utils/toast';
import { selectUsername, useCreds } from 'redux/global';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { store } from 'redux/store';
import * as io from 'socket.io-client';
import { getApiUrl } from 'utils';
import { fetchManagerReports } from 'pages/Reporting/redux';
import { notifyFormsInboxRefresh } from 'pages/Forms/formsInboxCountsHook';
import { fetchCustomMasterData, fetchErpRecords, fetchErpOperations, fetchErpDirectives } from 'pages/Records/redux';
import { selectUserEmail } from 'pages/app/redux';

const socket = io.connect(
  window.location.hostname === (process.env.REACT_APP_MOBILE_APP_URL || '').split(':8200').join('')
    .split('http://').join('').split('/').join('') ? process.env.REACT_APP_MOBILE_APP_URL || '' :
    getApiUrl(),
  {
    transports: ['polling', 'websocket'],
    reconnection: true,
    reconnectionAttempts: 20,
    reconnectionDelay: 1000,
  }
);

export const useOrganization = () => {
  const dispatch = useAppDispatch();

  const updateOrganization = (organizationData1: OrganizationData) => {
    const current = (store.getState() as any).views?.organizationData as OrganizationData | null;
    if (!current || !organizationData1 || typeof organizationData1 !== 'object') return;
    // Shallow merge; inventory/prefs patches from sockets replace those fields.
    dispatch(setOrganizationData({ ...current, ...organizationData1 }));
  };
  return { updateOrganization };
};

export const useSocketEmit = () => {
  const organizationData = useAppSelector(selectOrganizationData);

  const socketEmit = (type: string, data: Record<string, any>) => {
    socket.emit(type, {
      roomId: `room${organizationData?.id}`,
      ...data,
      senderId: socket.id
    });
  };

  return socketEmit;
};
export const useSocketTypeEmit = () => {
  const organizationData = useAppSelector(selectOrganizationData);

  const settingsEmit = (part: Record<string, any>) => {
    const settings = { ...part };
    // Data-URI photos blow past socket.io's packet cap and the whole update is dropped.
    if (Array.isArray(settings.users)) {
      settings.users = settings.users.map((user: { photo?: string | null }) => {
        const photo = user?.photo;
        if (typeof photo === 'string' && (photo.startsWith('data:') || photo.startsWith('blob:'))) {
          return { ...user, photo: null };
        }
        return user;
      });
    }
    socket.emit('update_settings', {
      roomId: `room${organizationData?.id}`,
      settings,
      senderId: socket.id
    });
  };

  return { settingsEmit };
};

export const useParentEffects = () => {
  const location = useLocation();
  const logs = useAppSelector(selectLogs);
  const mergedLogs = useAppSelector(selectActivityLogsMergedLogs);
  const dispatch = useAppDispatch();
  const savedUsername = useAppSelector(selectUsername);
  const userEmail = useAppSelector(selectUserEmail);
  const { addLocalRoom, editLocalRoom, deleteLocalRooms } = useLocalRoomUpdate();
  const {
    deleteLocalLog, updateLocalLog, fetchBranchFiles, updateLocalBranch, deleteLocalBranch,
    deleteLocalBranchFile, deleteLocalBranchFiles,
    deleteLocalLogType, logGroups
  } = useLogs();
  const { updateOrganization } = useOrganization();
  const { updateLocalSettings } = useUpdateSettings();
  const { logout } = useCreds();

  // Keep socket handlers stable — rebinding on every logs update caused black flashes / chart loss.
  const socketHandlersRef = useRef({
    addLocalRoom,
    editLocalRoom,
    deleteLocalRooms,
    deleteLocalLog,
    updateLocalLog,
    fetchBranchFiles,
    updateLocalBranch,
    deleteLocalBranch,
    deleteLocalBranchFile,
    deleteLocalBranchFiles,
    deleteLocalLogType,
    updateOrganization,
    updateLocalSettings,
    logout,
    savedUsername,
    userEmail,
    logGroups,
  });
  socketHandlersRef.current = {
    addLocalRoom,
    editLocalRoom,
    deleteLocalRooms,
    deleteLocalLog,
    updateLocalLog,
    fetchBranchFiles,
    updateLocalBranch,
    deleteLocalBranch,
    deleteLocalBranchFile,
    deleteLocalBranchFiles,
    deleteLocalLogType,
    updateOrganization,
    updateLocalSettings,
    logout,
    savedUsername,
    userEmail,
    logGroups,
  };

  useEffect(() => {
    // ===== GLOBAL SELF-MESSAGE BLOCK =====
    const originalOn = socket.on.bind(socket);

    // eslint-disable-next-line no-unused-vars
    socket.on = (event: string, handler: (...args: any[]) => void) => {
      return originalOn(event, (data: any, ...rest: any[]) => {
        // ignore messages sent by this same socket
        if (
          data &&
          typeof data === 'object' &&
          data.senderId === socket.id
        ) {
          return;
        }

        // optional unwrap if you wrapped payloads
        if (data && typeof data === 'object' && 'payload' in data) {
          return handler(data.payload, ...rest);
        }

        return handler(data, ...rest);
      });
    };
    // =====================================

    socket.on('connect_error', () => {
      dispatch(setIsSocketConnected(false));
    });

    socket.on('disconnect', () => {
      dispatch(setIsSocketConnected(false));
    });

    socket.on('connect', () => {
      dispatch(setSocket(socket));
      dispatch(setIsSocketConnected(true));

      // Ensure the client rejoins its room after a reconnect/backend restart.
      const organizationId = (store.getState() as any).views?.organizationData?.id;
      if (organizationId) {
        try {
          socket.emit('join_room', `room${organizationId}`);
        } catch (e) {
          // ignore emit errors
        }
      }
    });

    socket.on('get_added_room', (room) => {
      socketHandlersRef.current.addLocalRoom(room);
    });

    socket.on('get_edited_room', (rooms1) => {
      socketHandlersRef.current.editLocalRoom(rooms1);
    });

    socket.on('get_deleted_rooms', (ids) => {
      socketHandlersRef.current.deleteLocalRooms(ids);
    });

    socket.on('get_revoked_staff', (username) => {
      const currentUsername = socketHandlersRef.current.savedUsername;
      if (currentUsername?.replace(/\s+/g, '') === username.replace(/\s+/g, '')) {
        toast('Your access is denied', { type: 'error' });
        dispatch(setIsStaff(false));
        dispatch(socketHandlersRef.current.logout());
      }
    });

    socket.on('get_added_log', (logEntry) => {
      const { logs: logsH, checked } = store.getState().logs;
      const alreadyInPage = Boolean(
        logsH && logEntry?.id != null
        && logsH.find((log) => log.id.toString() === logEntry.id.toString()),
      );
      if (logsH && logEntry?.id != null && !alreadyInPage) {
        dispatch(setLogs([...logsH, logEntry]));
      }
      if (!alreadyInPage && logEntry?.id != null) {
        const org = store.getState().views.organizationData;
        if (org) {
          dispatch(setOrganizationData({
            ...org,
            activityLogsCount: Math.max(0, (Number(org.activityLogsCount) || 0) + 1),
          }));
        }
      }
      // Keep walk-in activityLogs visible for all staff when walk activity arrives
      if (logEntry?.type === 'Walk in' || logEntry?.type === 'Walk out') {
        const current: string[] = checked || [];
        const next = [
          ...current.filter((c: string) => c !== logEntry.type && c !== 'Walk-ins'),
          logEntry.type,
        ];
        if (next.length !== current.length
          || next.some((c, i) => c !== current[i])) {
          dispatch(setLogsChecked(next));
        }
      }
    });

    socket.on('get_deleted_log', (logId) => {
      if (store.getState().logs.logs) socketHandlersRef.current.deleteLocalLog(logId);
      const org = store.getState().views.organizationData;
      if (org) {
        dispatch(setOrganizationData({
          ...org,
          activityLogsCount: Math.max(0, (Number(org.activityLogsCount) || 0) - 1),
        }));
      }
    });

    socket.on('get_deleted_log_type', (type) => {
      if (store.getState().logs.logs) socketHandlersRef.current.deleteLocalLogType(type);
    });

    socket.on('get_updated_log', (log) => {
      if (store.getState().logs.logs) socketHandlersRef.current.updateLocalLog(log);
    });

    socket.on('get_updated_branchFiles', async (payload) => {
      const logsState = store.getState().logs.logs;
      if (!logsState) return;
      const branchFiles = store.getState().logs.branchFiles;
      const { action, branch, sheetName } = payload || {};
      if (!branch || !action) return;

      if (action === 'deleted') {
        if (sheetName) {
          socketHandlersRef.current.deleteLocalBranchFile(branch, sheetName);
        }
        return;
      }

      if (action === 'deleted_branch') {
        socketHandlersRef.current.deleteLocalBranchFiles(branch);
        return;
      }

      if (action === 'renamed_branch') {
        const previousName = typeof payload?.previousName === 'string'
          ? payload.previousName
          : '';
        const branchName = typeof branch === 'string' ? branch : '';
        if (previousName && branchName && branchFiles?.[previousName]) {
          const nextFiles: BranchFiles = {
            ...branchFiles,
            [branchName]: branchFiles[previousName],
          };
          delete nextFiles[previousName];
          dispatch(setBranchFiles(nextFiles));
        } else if (logsState) {
          socketHandlersRef.current.fetchBranchFiles(true);
        }
        // Mirror DB bulk rename of log department tags for other clients.
        if (previousName && branchName) {
          const { logs: currentLogs, checked } = store.getState().logs;
          if (currentLogs) {
            const rewritten = rewriteLogsForDepartmentRename(
              currentLogs,
              previousName,
              branchName,
            );
            if (rewritten) dispatch(setLogs(rewritten));
          }
          if (checked?.some((c: string) => c === previousName)) {
            dispatch(setLogsChecked(
              checked.map((c: string) => (c === previousName ? branchName : c)),
            ));
          }
        }
        return;
      }

      if (action === 'added' && sheetName) {
        const res = await dispatch(getBranchFile(branch, sheetName));
        if (res?.status !== 'success') return;
        const latestFiles = store.getState().logs.branchFiles;
        const newBranchFiles = {
          ...(latestFiles || {}),
          [branch]: {
            ...((latestFiles || {})[branch] || {}),
            [sheetName]: res.data.sheet,
          },
        };
        dispatch(setBranchFiles(newBranchFiles));
        return;
      }

      socketHandlersRef.current.fetchBranchFiles(true);
    });

    socket.on('get_updated_branch', (payload) => {
      const branch = payload?.branch || payload;
      const previousName = payload?.previousName;
      if (!branch) return;
      socketHandlersRef.current.updateLocalBranch(branch, previousName);
    });

    socket.on('get_deleted_branch', (branch) => {
      socketHandlersRef.current.deleteLocalBranch(branch);
    });

    socket.on('get_updated_organization', (organizationData1) => {
      socketHandlersRef.current.updateOrganization(organizationData1);
    });

    socket.on('get_updated_settings', (settings) => {
      socketHandlersRef.current.updateLocalSettings(settings);
    });

    socket.on('get_updated_crm_settings', async () => {
      try {
        const res = await dispatch(getInfo());
        if (res?.status === 'success' && res.data) {
          const incomingSettings = res.data.crmSettings ?? res.data;
          if (incomingSettings) {
            dispatch(setCRMSettings(incomingSettings));
          }
        }
      } catch (e) {
        // non-fatal; ignore
      }
    });

    socket.on('get_customer_deltas', ({ customers }) => {
      (customers || []).forEach((customer: Customer) => {
        dispatch(addOrUpdateCustomer(customer));
      });
    });

    // Recipient refreshes inbox when a new/cleared manager report arrives.
    socket.on('get_updated_manager_reports', (payload: { toEmail?: string }) => {
      const to = String(payload?.toEmail || '').toLowerCase();
      const me = String(socketHandlersRef.current.userEmail || '').toLowerCase();
      if (to && me && to !== me) return;
      dispatch(fetchManagerReports());
      notifyFormsInboxRefresh();
    });

    socket.on('get_updated_report_requests', (payload: { emails?: string[] }) => {
      const me = String(socketHandlersRef.current.userEmail || '').toLowerCase();
      if (!me) return;
      const emails = Array.isArray(payload?.emails)
        ? payload.emails.map((email) => String(email || '').toLowerCase())
        : [];
      if (emails.length && !emails.includes(me)) return;
      notifyFormsInboxRefresh();
    });

    socket.on('get_updated_records', () => {
      dispatch(fetchErpRecords());
    });

    socket.on('get_updated_custom_master_data', () => {
      dispatch(fetchCustomMasterData());
    });

    socket.on('get_updated_operations', () => {
      dispatch(fetchErpOperations());
      notifyFormsInboxRefresh();
    });

    socket.on('get_updated_directives', () => {
      dispatch(fetchErpDirectives());
    });

    return () => {
      socket.off();
      socket.on = originalOn; // restore original
    };
    // Bind once — handlers read latest values via ref + store (avoid rebind on every log update).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    setTimeout(() => {
      document.getElementById('main_cont')?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 140);
  }, [location]);

  // Rebuild metric buckets whenever raw logs change. Do NOT flip
  // isLoadingLogs back to true here — that unmounts the page (holdChildren)
  // and looked like repeated refreshes when branch-file logs merged in.
  useEffect(() => {
    const pool = mergedLogs?.length ? mergedLogs : logs;
    if (!pool) return;
    dispatch(setMetricLogs(aggregateLogsToMetricLogs(pool)));
    dispatch(setIsLoadingLogs(false));
  }, [logs, mergedLogs, dispatch]);
};
